import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, File, SpinnerGap } from '../components/ui/icons'
import { OrderFormDocument } from '../components/OrderFormDocument'
import { adminPreviewById, publicPreviewById } from '../data/previews'
import { api } from '../lib/api'
import { ORDER_FORM_CUSTOMER_STAGE_LABEL, ORDER_FORM_STAGE_BADGE, ORDER_FORM_STAGE_LABEL, orderFormStage, signedDocuments } from '../lib/order-form'
import { StatusBadge } from '../components/ui'
import type { AdminWorkspace, OrderForm, PublicWorkspace } from '../types'
import { NotFoundPage } from './NotFoundPage'

/**
 * A page with nothing on it but the order form and a toolbar. Customers reach
 * it from their Order Form tab, admins from the editor. Printing it is how a
 * PDF is produced today; the same document is what the e-signature step will
 * send later, so keep this route free of anything that is not the document.
 */
export function OrderFormPrintPage({ admin = false }: { admin?: boolean }) {
  const { publicId = '', workspaceId = '', formId = '' } = useParams()
  const lookup = admin ? workspaceId : publicId
  const [workspace, setWorkspace] = useState<PublicWorkspace | AdminWorkspace | null>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'missing' | 'auth'>('loading')

  useEffect(() => {
    const preview = import.meta.env.DEV ? (admin ? adminPreviewById(lookup) : publicPreviewById(lookup)) : null
    if (preview) { setWorkspace(preview); setState('ready'); return }
    const load = admin ? api.loadAdminWorkspace(lookup) : api.loadWorkspace(lookup)
    load.then((data) => { setWorkspace(data); setState('ready') })
      .catch((cause: Error & { status?: number }) => setState(cause.status === 404 ? 'missing' : 'auth'))
  }, [admin, lookup])

  useEffect(() => {
    const previousTitle = document.title
    return () => { document.title = previousTitle }
  }, [])

  if (state === 'loading') return <main className="state-page"><div className="loading-lockup"><SpinnerGap size={21} className="spin" /><span>Opening order form</span></div></main>
  if (state === 'missing') return <NotFoundPage />
  const backHref = admin ? `/admin/workspaces/${workspaceId}` : `/${publicId}`
  const preview = Boolean(import.meta.env.DEV && (admin ? adminPreviewById(lookup) : publicPreviewById(lookup)))
  const pdfHref = admin ? `/api/admin/workspaces/${workspaceId}/order-forms/${formId}/pdf` : `/api/workspaces/${publicId}/order-forms/${formId}/pdf`
  if (state === 'auth' || !workspace) {
    return (
      <main className="state-page">
        <section className="state-panel">
          <h1>Open the workspace first</h1>
          <p>{admin ? 'Sign in with a Prentus account to view this order form.' : 'Verify your work email in the workspace, then open the order form again.'}</p>
          <Link className="button button-primary" to={backHref}>Go to workspace</Link>
        </section>
      </main>
    )
  }
  const form: OrderForm | undefined = workspace.orderForms.find((entry) => entry.id === formId)
  if (!form) return <NotFoundPage />
  const { signedCopy } = signedDocuments(form, workspace.assets)
  document.title = `${form.number} · ${form.title || workspace.accountName}`

  return (
    <main className="order-form-page">
      <div className="order-form-toolbar">
        <Link className="back-link" to={backHref}><ArrowLeft size={15} />Back to workspace</Link>
        <StatusBadge tone={ORDER_FORM_STAGE_BADGE[orderFormStage(form)]}>{(admin ? ORDER_FORM_STAGE_LABEL : ORDER_FORM_CUSTOMER_STAGE_LABEL)[orderFormStage(form)]}</StatusBadge>
        <span className="order-form-action-links">
          <button type="button" className="button button-secondary compact" onClick={() => window.print()}>Print</button>
          <a className="button button-primary compact" href={`${pdfHref}?download=1`}><File size={15} />Download PDF</a>
        </span>
      </div>
      {signedCopy?.url
        ? <div className="order-form-signed-embed page"><iframe src={`${signedCopy.url}#toolbar=1&view=FitH`} title={`Signed order form ${form.number}`} /></div>
        : preview
          ? <OrderFormDocument form={form} accountName={workspace.accountName} accountLogoUrl={workspace.accountLogoUrl} assets={workspace.assets} />
          : <div className="order-form-signed-embed page"><iframe src={`${pdfHref}#toolbar=1&view=FitH`} title={`Order form ${form.number}`} /></div>}
    </main>
  )
}
